import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Money } from "./SaudiRiyal";
import {
  Gift, Award, Trophy, Crown, Star, Plus, Minus, History, Trash2, Loader2,
  Coins, Sparkles, Users, TrendingDown, Save, Building2, Zap, Medal,
  HandCoins, ClipboardList, CheckCircle2, XCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PartnerOffersPanel } from "./PartnerOffersPanel";
import { personMatch } from "../lib/searchText";

const money = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ⃁`;
const pts = (n) => Number(n || 0).toLocaleString("en-US");

const TIERS = {
  bronze: { name: "برونزي", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Star },
  silver: { name: "فضي", cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: Award },
  gold: { name: "ذهبي", cls: "bg-yellow-100 text-yellow-800 border-yellow-300", Icon: Trophy },
  platinum: { name: "بلاتيني", cls: "bg-indigo-100 text-indigo-800 border-indigo-300", Icon: Crown },
};
const TierBadge = ({ tier }) => {
  const T = TIERS[tier?.key] || TIERS.bronze;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${T.cls}`}>
      <T.Icon className="h-3 w-3" />{T.name}
    </span>
  );
};

const AwardDialog = ({ member, behaviors = [], onClose, onSaved }) => {
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const p = parseInt(points, 10);
    if (!p || p <= 0) { toast.error("أدخل عدد نقاط صحيح"); return; }
    setBusy(true);
    try { await api.loyaltyAward(member.employee_id, p, reason); toast.success(`تمت إضافة ${p} نقطة لـ ${member.name}`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر منح النقاط"); }
    finally { setBusy(false); }
  };
  const awardBehavior = async (b) => {
    setBusy(true);
    try { await api.loyaltyAwardBehavior(member.employee_id, b.id); toast.success(`تمت إضافة ${b.points} نقطة (${b.title}) لـ ${member.name}`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر منح النقاط"); }
    finally { setBusy(false); }
  };
  const quick = [50, 100, 250, 500];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="loyalty-award-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg flex items-center gap-2"><Gift className="h-5 w-5 text-emerald-600" /> منح نقاط · {member.name}</DialogTitle>
          <DialogDescription className="text-start">كافئ الموظف على أدائه المتميز.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {behaviors.length > 0 && (
            <div className="space-y-2 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3" data-testid="award-behaviors">
              <p className="text-[11px] font-semibold text-emerald-700 flex items-center gap-1"><HandCoins className="h-3.5 w-3.5" /> منح سلوك بضغطة</p>
              <div className="flex flex-wrap gap-1.5">
                {behaviors.map((b) => (
                  <button key={b.id} type="button" disabled={busy} data-testid={`award-behavior-${b.id}`} onClick={() => awardBehavior(b)}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition disabled:opacity-50">
                    {b.title} <span className="font-mono-nums font-bold">+{b.points}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {quick.map((q) => (
              <button key={q} type="button" data-testid={`loyalty-quick-${q}`} onClick={() => setPoints(String(q))}
                className={`rounded-full border px-3 py-1 text-sm font-mono-nums transition ${String(q) === points ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-emerald-300"}`}>+{q}</button>
            ))}
          </div>
          <label className="block"><span className="text-[11px] text-slate-500">عدد النقاط (منح مخصّص)</span>
            <Input type="number" dir="ltr" value={points} onChange={(e) => setPoints(e.target.value)} className="h-9 font-mono-nums" data-testid="loyalty-award-points" /></label>
          <label className="block"><span className="text-[11px] text-slate-500">السبب (اختياري)</span>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="مثال: تجاوز هدف الطلبات، التزام ممتاز..." data-testid="loyalty-award-reason" /></label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
          <Button onClick={save} disabled={busy} data-testid="loyalty-award-save" className="bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "منح النقاط"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RedeemDialog = ({ member, rewards, onClose, onSaved }) => {
  const [rewardId, setRewardId] = useState("");
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const pickReward = (r) => { setRewardId(r.id); setPoints(String(r.cost)); setReason(r.title); };
  const save = async () => {
    const p = parseInt(points, 10);
    if (!p || p <= 0) { toast.error("أدخل عدد نقاط صحيح"); return; }
    if (p > member.balance) { toast.error(`الرصيد غير كافٍ (${member.balance} نقطة)`); return; }
    setBusy(true);
    try { await api.loyaltyRedeem({ employee_id: member.employee_id, points: p, reason, reward_id: rewardId || null }); toast.success(`تم صرف ${p} نقطة لـ ${member.name}`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر صرف النقاط"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="loyalty-redeem-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg flex items-center gap-2"><Coins className="h-5 w-5 text-amber-600" /> صرف مكافأة · {member.name}</DialogTitle>
          <DialogDescription className="text-start">الرصيد المتاح: <b className="font-mono-nums text-slate-700">{pts(member.balance)}</b> نقطة</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {rewards.length > 0 && (
            <div className="space-y-2">
              <span className="text-[11px] text-slate-500">اختر من كتالوج المكافآت</span>
              <div className="grid grid-cols-1 gap-2">
                {rewards.map((r) => {
                  const afford = member.balance >= r.cost;
                  return (
                    <button key={r.id} type="button" data-testid={`loyalty-pick-reward-${r.id}`} onClick={() => afford && pickReward(r)} disabled={!afford}
                      className={`flex items-center justify-between rounded-xl border px-3 py-2 text-start transition ${rewardId === r.id ? "border-amber-400 bg-amber-50" : afford ? "border-slate-200 hover:border-amber-300" : "border-slate-100 opacity-50 cursor-not-allowed"}`}>
                      <div><p className="text-sm font-semibold text-slate-800">{r.title}</p>{r.description && <p className="text-[11px] text-slate-400">{r.description}</p>}</div>
                      <span className="shrink-0 font-mono-nums text-sm font-bold text-amber-700">{pts(r.cost)} نقطة</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <label className="block"><span className="text-[11px] text-slate-500">عدد النقاط المصروفة</span>
            <Input type="number" dir="ltr" value={points} onChange={(e) => { setPoints(e.target.value); setRewardId(""); }} className="h-9 font-mono-nums" data-testid="loyalty-redeem-points" /></label>
          <label className="block"><span className="text-[11px] text-slate-500">الوصف / المكافأة</span>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="h-9 text-sm" placeholder="مثال: بطاقة وقود، مكافأة نقدية..." data-testid="loyalty-redeem-reason" /></label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={save} disabled={busy} data-testid="loyalty-redeem-save" className="bg-amber-600 hover:bg-amber-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "صرف النقاط"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HistoryDialog = ({ member, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.loyaltyHistory(member.employee_id).then((d) => setRows(d.ledger || [])).catch(() => setRows([])); }, [member.employee_id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md font-body max-h-[85vh] overflow-y-auto" dir="rtl" data-testid="loyalty-history-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg flex items-center gap-2"><History className="h-5 w-5 text-blue-600" /> سجل نقاط · {member.name}</DialogTitle>
          <DialogDescription className="sr-only">سجل حركات النقاط</DialogDescription></DialogHeader>
        {!rows ? <div className="py-8 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          : rows.length === 0 ? <p className="py-8 text-center text-slate-400 text-sm">لا توجد حركات بعد.</p>
            : <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-700 truncate">{r.reason || (r.type === "earn" ? "نقاط أداء" : "صرف مكافأة")}{r.reward_title ? ` · ${r.reward_title}` : ""}</p>
                    <p className="text-[10px] text-slate-400">{(r.created_at || "").slice(0, 10)} · {r.created_by}</p>
                  </div>
                  <span className={`font-mono-nums font-bold ${r.points >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{r.points >= 0 ? "+" : ""}{pts(r.points)}</span>
                </div>
              ))}
            </div>}
      </DialogContent>
    </Dialog>
  );
};

const CompanyRedeemDialog = ({ company, onClose, onSaved }) => {
  const [points, setPoints] = useState("");
  const [busy, setBusy] = useState(false);
  const rate = company.redeem_per_sar || 20;
  const p = parseInt(points, 10) || 0;
  const sar = p / rate;
  const save = async () => {
    if (!p || p <= 0 || p > company.points) { toast.error("عدد نقاط غير صالح"); return; }
    if (p % rate !== 0) { toast.error(`استبدل بمضاعفات ${rate} نقطة`); return; }
    setBusy(true);
    try { const r = await api.loyaltyCompanyRedeem(p); toast.success(`تمت إضافة ${money(r.sar_added)} رصيد خصم`); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الاستبدال"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm font-body" dir="rtl" data-testid="company-redeem-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-indigo-600" /> استبدال نقاط ولاء الشركة</DialogTitle>
          <DialogDescription className="text-start">حوّل نقاطك إلى رصيد خصم يُخصم من فاتورة التجديد القادمة. كل {rate} نقطة = 1 ⃁.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className="text-[11px] text-slate-500">النقاط المراد استبدالها (المتاح {pts(company.points)})</span>
            <Input type="number" dir="ltr" value={points} onChange={(e) => setPoints(e.target.value)} className="h-9 font-mono-nums" data-testid="company-redeem-points" /></label>
          <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3 text-center">
            <p className="text-[11px] text-indigo-700">رصيد الخصم الناتج</p>
            <p className="font-heading text-2xl font-bold text-indigo-700 font-mono-nums"><Money value={sar} decimals /></p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={save} disabled={busy} data-testid="company-redeem-save" className="bg-indigo-600 hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "استبدال"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AutoAwardDialog = ({ month, onClose, onDone }) => {
  const [m, setM] = useState(month);
  const [bonus, setBonus] = useState("100");
  const [perExtra, setPerExtra] = useState("0");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.loyaltyAutoAward({ month: m, target_bonus: parseInt(bonus, 10) || 0, points_per_extra_order: parseInt(perExtra, 10) || 0 });
      setResult(r);
      if (r.message) toast.info(r.message);
      else toast.success(`تم منح ${r.awarded.length} مندوب (${pts(r.total_points)} نقطة)`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر المنح التلقائي"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="auto-award-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-lg flex items-center gap-2"><Zap className="h-5 w-5 text-amber-500" /> منح تلقائي حسب الطلبات</DialogTitle>
          <DialogDescription className="text-start">يمنح النظام نقاطًا لكل مندوب تجاوز هدف الطلبات الشهري. المنح آمن ولا يتكرر لنفس الشهر.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <label className="block"><span className="text-[11px] text-slate-500">الشهر</span>
              <Input type="month" value={m} onChange={(e) => setM(e.target.value)} className="h-9 text-sm" data-testid="auto-award-month" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">مكافأة التجاوز</span>
              <Input type="number" dir="ltr" value={bonus} onChange={(e) => setBonus(e.target.value)} className="h-9 font-mono-nums text-sm" data-testid="auto-award-bonus" /></label>
            <label className="block"><span className="text-[11px] text-slate-500">لكل طلب إضافي</span>
              <Input type="number" dir="ltr" value={perExtra} onChange={(e) => setPerExtra(e.target.value)} className="h-9 font-mono-nums text-sm" data-testid="auto-award-per-extra" /></label>
          </div>
          <p className="text-[11px] text-slate-400">النقاط = مكافأة التجاوز + (الطلبات فوق الهدف × نقاط لكل طلب إضافي). حدّد هدف الطلبات من إعدادات الشركة.</p>
          {result && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="auto-award-result">
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="text-emerald-700 font-semibold">مُنح: {result.awarded.length}</span>
                <span className="text-slate-500">تم سابقًا: {result.skipped_already}</span>
                <span className="text-slate-500">بدون هدف: {result.no_target}</span>
                <span className="text-blue-700 font-semibold">الإجمالي: {pts(result.total_points)} نقطة</span>
              </div>
              {result.awarded.slice(0, 8).map((a) => (
                <div key={a.employee_id} className="flex items-center justify-between text-sm border-b border-slate-100 py-1">
                  <span className="text-slate-700 truncate">{a.name} <span className="text-[10px] text-slate-400 font-mono-nums">({a.completed}/{a.target})</span></span>
                  <span className="font-mono-nums font-bold text-emerald-700">+{pts(a.points)}</span>
                </div>
              ))}
              {result.awarded.length === 0 && !result.message && <p className="text-center text-slate-400 text-sm py-1">لا يوجد من تجاوز الهدف.</p>}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
          <Button onClick={run} disabled={busy} data-testid="auto-award-run" className="bg-amber-500 hover:bg-amber-600 gap-1.5"><Zap className="h-4 w-4" />{busy ? "..." : "تشغيل المنح"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MEDAL = { 1: "from-yellow-400 to-amber-500", 2: "from-slate-300 to-slate-400", 3: "from-amber-600 to-orange-700" };
const Leaderboard = ({ month, setMonth, board, canEdit, onAuto }) => (
  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-amber-50/40 p-4" data-testid="loyalty-leaderboard">
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <p className="font-heading font-bold text-slate-800 flex items-center gap-1.5"><Trophy className="h-5 w-5 text-amber-500" /> أفضل المندوبين نقاطًا هذا الشهر</p>
      <div className="flex items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-36 text-sm" data-testid="leaderboard-month" />
        {canEdit && <Button onClick={onAuto} size="sm" data-testid="auto-award-open" className="bg-amber-500 hover:bg-amber-600 gap-1.5 h-8"><Zap className="h-4 w-4" /> منح تلقائي</Button>}
      </div>
    </div>
    {board.length === 0 ? (
      <p className="py-6 text-center text-slate-400 text-sm" data-testid="leaderboard-empty">لا توجد نقاط مُكتسبة في هذا الشهر بعد. شغّل «منح تلقائي» أو امنح نقاطًا يدويًا.</p>
    ) : (
      <div className="space-y-2">
        {board.slice(0, 10).map((b) => (
          <div key={b.employee_id} data-testid={`leaderboard-row-${b.rank}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${b.rank <= 3 ? "border-amber-200 bg-white shadow-sm" : "border-slate-100 bg-white"}`}>
            <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full font-bold font-mono-nums text-sm ${b.rank <= 3 ? `bg-gradient-to-br ${MEDAL[b.rank]} text-white` : "bg-slate-100 text-slate-500"}`}>
              {b.rank <= 3 ? <Medal className="h-4 w-4" /> : b.rank}
            </div>
            <div className="min-w-0 flex-1"><p className="font-semibold text-slate-800 truncate">{b.name}</p>{b.branch && <p className="text-[10px] text-slate-400">{b.branch}</p>}</div>
            <span className="font-mono-nums font-bold text-amber-700 shrink-0">{pts(b.points)} نقطة</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const LoyaltyPanel = ({ canEdit }) => {
  const { subscription } = useAuth();
  const isOwner = Boolean(subscription?.is_platform_owner) || subscription?.plan === undefined;
  const [tab, setTab] = useState("members");
  const [data, setData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [search, setSearch] = useState("");
  const [award, setAward] = useState(null);
  const [redeem, setRedeem] = useState(null);
  const [history, setHistory] = useState(null);
  const [companyRedeem, setCompanyRedeem] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [board, setBoard] = useState([]);
  const [autoOpen, setAutoOpen] = useState(false);
  const [pointVal, setPointVal] = useState("");
  const [savingVal, setSavingVal] = useState(false);
  const [newReward, setNewReward] = useState({ title: "", cost: "", description: "" });
  const [busyReward, setBusyReward] = useState(false);
  const [behaviors, setBehaviors] = useState([]);
  const [newBehavior, setNewBehavior] = useState({ title: "", points: "", description: "" });
  const [busyBehavior, setBusyBehavior] = useState(false);
  const [requests, setRequests] = useState([]);
  const [busyReq, setBusyReq] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, rw, bh, rq] = await Promise.all([
        api.loyaltyOverview(), api.loyaltyRewards(), api.loyaltyBehaviors(), api.loyaltyRedeemRequests(),
      ]);
      setData(d); setRewards(rw.rewards || []); setBehaviors(bh.behaviors || []);
      setRequests(rq.requests || []); setPointVal(String(d.rules?.point_value_sar || ""));
    } catch { toast.error("تعذّر تحميل نقاط الولاء"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadBoard = useCallback(async () => {
    try { const b = await api.loyaltyLeaderboard(month); setBoard(b.leaderboard || []); } catch { /* ignore */ }
  }, [month]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const saveVal = async () => {
    setSavingVal(true);
    try { await api.loyaltyRules(parseFloat(pointVal) || 0); toast.success("تم حفظ قيمة النقطة"); load(); }
    catch { toast.error("تعذّر الحفظ"); } finally { setSavingVal(false); }
  };
  const addReward = async () => {
    const cost = parseInt(newReward.cost, 10);
    if (!newReward.title.trim() || !cost || cost <= 0) { toast.error("أدخل اسم المكافأة والتكلفة بالنقاط"); return; }
    setBusyReward(true);
    try { await api.createReward({ title: newReward.title.trim(), cost, description: newReward.description.trim() }); toast.success("تمت إضافة المكافأة"); setNewReward({ title: "", cost: "", description: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإضافة"); } finally { setBusyReward(false); }
  };
  const delReward = async (id) => { if (!window.confirm("حذف هذه المكافأة؟")) return; try { await api.deleteReward(id); load(); } catch { toast.error("تعذّر الحذف"); } };

  const addBehavior = async () => {
    const points = parseInt(newBehavior.points, 10);
    if (!newBehavior.title.trim() || !points || points <= 0) { toast.error("أدخل اسم السلوك وعدد النقاط"); return; }
    setBusyBehavior(true);
    try { await api.createBehavior({ title: newBehavior.title.trim(), points, description: newBehavior.description.trim() }); toast.success("تمت إضافة السلوك"); setNewBehavior({ title: "", points: "", description: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإضافة"); } finally { setBusyBehavior(false); }
  };
  const delBehavior = async (id) => { if (!window.confirm("حذف هذا السلوك؟")) return; try { await api.deleteBehavior(id); load(); } catch { toast.error("تعذّر الحذف"); } };
  const decideReq = async (id, approve) => {
    setBusyReq(id);
    try { const r = await api.decideRedeemRequest(id, approve); toast.success(approve ? "تم اعتماد الاستبدال وخصم النقاط" : "تم رفض الطلب"); load(); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تنفيذ الطلب"); } finally { setBusyReq(null); }
  };

  const members = (data?.members || []).filter((m) => personMatch(m, search));
  const totals = data?.totals || {};
  const company = data?.company || {};
  const ptVal = data?.rules?.point_value_sar || 0;

  if (!data) return <div className="py-16 text-center text-slate-300" data-testid="loyalty-loading"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="loyalty-panel" dir="rtl">
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-rose-500 text-white shadow-sm"><Gift className="h-5 w-5" /></div>
        <div><h3 className="font-heading font-bold text-slate-800">الولاء والمكافآت</h3>
          <p className="text-[11px] text-slate-400">كافئ فريقك على الأداء، واكسب مزايا للشركة مع كل تجديد.</p></div>
      </div>

      {/* Company loyalty banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-900 p-5 text-white" data-testid="company-loyalty-card">
        <Sparkles className="absolute -left-4 -top-4 h-28 w-28 text-white/5" />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs text-indigo-200 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> برنامج ولاء الشركة على المنصة</p>
            <div className="mt-2 flex items-end gap-3">
              <p className="font-heading text-4xl font-bold font-mono-nums" data-testid="company-points">{pts(company.points)}</p>
              <span className="mb-1 text-indigo-200 text-sm">نقطة</span>
              <span className="mb-1"><TierBadge tier={company.tier} /></span>
            </div>
            <p className="mt-1 text-xs text-indigo-200">تكسب نقاطًا مع كل تجديد اشتراك — كل {company.redeem_per_sar} نقطة = 1 ⃁ خصم.</p>
          </div>
          <div className="flex flex-col items-stretch gap-2 min-w-[180px]">
            <div className="rounded-xl bg-white/10 backdrop-blur px-4 py-2 text-center">
              <p className="text-[11px] text-indigo-200">رصيد خصم متاح</p>
              <p className="font-heading text-xl font-bold font-mono-nums" data-testid="company-credit"><Money value={company.credit_sar} decimals /></p>
            </div>
            {isOwner && (
              <Button onClick={() => setCompanyRedeem(true)} disabled={!company.points} data-testid="company-redeem-btn"
                className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold">استبدال بخصم</Button>
            )}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4" data-testid="loyalty-kpi-circulation">
          <p className="text-xs text-emerald-700 flex items-center gap-1"><Coins className="h-3.5 w-3.5" /> نقاط متداولة</p>
          <p className="font-heading text-2xl font-bold text-emerald-700 font-mono-nums mt-1">{pts(totals.points)}</p>
          {ptVal > 0 && <p className="text-[10px] text-emerald-600 mt-0.5">≈ <Money value={(totals.points || 0) * ptVal} decimals /></p>}
        </div>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4" data-testid="loyalty-kpi-members">
          <p className="text-xs text-blue-700 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> أعضاء نشطون</p>
          <p className="font-heading text-2xl font-bold text-blue-700 font-mono-nums mt-1">{pts(totals.active_members)}</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4" data-testid="loyalty-kpi-redeemed">
          <p className="text-xs text-amber-700 flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" /> نقاط مصروفة</p>
          <p className="font-heading text-2xl font-bold text-amber-700 font-mono-nums mt-1">{pts(totals.redeemed)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">قيمة النقطة (⃁)</p>
          <div className="mt-1 flex items-center gap-1.5">
            <Input type="number" dir="ltr" step="0.01" value={pointVal} onChange={(e) => setPointVal(e.target.value)} className="h-8 w-20 font-mono-nums text-sm" data-testid="loyalty-point-value" disabled={!canEdit} />
            {canEdit && <button onClick={saveVal} disabled={savingVal} data-testid="loyalty-point-value-save" className="grid h-8 w-8 place-items-center rounded-md bg-slate-800 text-white hover:bg-slate-700">{savingVal ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</button>}
          </div>
        </div>
      </div>

      <Leaderboard month={month} setMonth={setMonth} board={board} canEdit={canEdit} onAuto={() => setAutoOpen(true)} />

      {/* sub tabs */}
      <div className="flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 p-1 w-fit">
        <button data-testid="loyalty-tab-members" onClick={() => setTab("members")} className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "members" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>الموظفون والنقاط</button>
        <button data-testid="loyalty-tab-behaviors" onClick={() => setTab("behaviors")} className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "behaviors" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>سلوكيات الكسب</button>
        <button data-testid="loyalty-tab-rewards" onClick={() => setTab("rewards")} className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "rewards" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>كتالوج المكافآت</button>
        <button data-testid="loyalty-tab-requests" onClick={() => setTab("requests")} className={`px-3 py-1.5 text-sm rounded-md transition flex items-center gap-1 ${tab === "requests" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>طلبات الاستبدال
          {requests.length > 0 && <span data-testid="requests-badge" className="rounded-full bg-rose-500 text-white text-[10px] font-bold px-1.5 min-w-[18px] text-center">{requests.length}</span>}
        </button>
        <button data-testid="loyalty-tab-offers" onClick={() => setTab("offers")} className={`px-3 py-1.5 text-sm rounded-md transition ${tab === "offers" ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>عروض شركاء النجاح</button>
      </div>

      {tab === "members" ? (
        <>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ابحث بالاسم أو رقم المندوب..." className="h-9 max-w-xs text-sm" data-testid="loyalty-search" />
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm text-right min-w-[640px]">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="px-3 py-2.5 font-semibold w-8">#</th>
                  <th className="px-3 py-2.5 font-semibold">الموظف</th>
                  <th className="px-3 py-2.5 font-semibold">الفئة</th>
                  <th className="px-3 py-2.5 font-semibold">الرصيد</th>
                  <th className="px-3 py-2.5 font-semibold">إجمالي مكتسب</th>
                  {canEdit && <th className="px-3 py-2.5 font-semibold w-32"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400" data-testid="loyalty-empty">لا يوجد موظفون.</td></tr>
                ) : members.map((m, i) => (
                  <tr key={m.employee_id} data-testid={`loyalty-row-${m.employee_id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2.5 text-slate-400 font-mono-nums">{i + 1}</td>
                    <td className="px-3 py-2.5"><p className="font-semibold text-slate-800">{m.name}</p>{m.branch && <p className="text-[10px] text-slate-400">{m.branch}</p>}</td>
                    <td className="px-3 py-2.5"><TierBadge tier={m.tier} /></td>
                    <td className="px-3 py-2.5 font-mono-nums font-bold text-emerald-700" data-testid={`loyalty-balance-${m.employee_id}`}>{pts(m.balance)}</td>
                    <td className="px-3 py-2.5 font-mono-nums text-slate-500">{pts(m.earned)}</td>
                    {canEdit && (
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          <button data-testid={`loyalty-award-btn-${m.employee_id}`} onClick={() => setAward(m)} title="منح" className="grid h-7 w-7 place-items-center rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><Plus className="h-4 w-4" /></button>
                          <button data-testid={`loyalty-redeem-btn-${m.employee_id}`} onClick={() => setRedeem(m)} disabled={!m.balance} title="صرف" className="grid h-7 w-7 place-items-center rounded-md bg-amber-50 text-amber-600 hover:bg-amber-100 disabled:opacity-40"><Minus className="h-4 w-4" /></button>
                          <button data-testid={`loyalty-history-btn-${m.employee_id}`} onClick={() => setHistory(m)} title="السجل" className="grid h-7 w-7 place-items-center rounded-md bg-slate-100 text-slate-500 hover:bg-slate-200"><History className="h-4 w-4" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : tab === "behaviors" ? (
        <div className="space-y-4" data-testid="loyalty-behaviors-tab">
          {canEdit && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 space-y-2">
              <p className="text-sm font-semibold text-slate-700">إضافة سلوك يكسب نقاط</p>
              <p className="text-[11px] text-slate-400">عرّف سلوكيات مثل: نظافة المركبة، الالتزام بالزي، عدم التأخر — ثم امنحها للموظف بضغطة من نافذة «منح النقاط».</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={newBehavior.title} onChange={(e) => setNewBehavior((v) => ({ ...v, title: e.target.value }))} placeholder="اسم السلوك" className="h-9 text-sm" data-testid="behavior-title" />
                <Input type="number" dir="ltr" value={newBehavior.points} onChange={(e) => setNewBehavior((v) => ({ ...v, points: e.target.value }))} placeholder="النقاط الممنوحة" className="h-9 text-sm font-mono-nums" data-testid="behavior-points" />
                <Input value={newBehavior.description} onChange={(e) => setNewBehavior((v) => ({ ...v, description: e.target.value }))} placeholder="وصف (اختياري)" className="h-9 text-sm" data-testid="behavior-desc" />
              </div>
              <div className="flex justify-end"><Button onClick={addBehavior} disabled={busyBehavior} size="sm" data-testid="behavior-add-btn" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"><Plus className="h-4 w-4" /> {busyBehavior ? "..." : "إضافة"}</Button></div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {behaviors.length === 0 ? <p className="col-span-full py-8 text-center text-slate-400 text-sm" data-testid="behaviors-empty">لا توجد سلوكيات بعد. أضف سلوكًا ليكسب منه فريقك النقاط.</p>
              : behaviors.map((b) => (
                <div key={b.id} data-testid={`behavior-card-${b.id}`} className="relative rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white"><HandCoins className="h-5 w-5" /></div>
                    {canEdit && <button onClick={() => delBehavior(b.id)} data-testid={`behavior-del-${b.id}`} className="grid h-7 w-7 place-items-center rounded-md text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                  <p className="mt-3 font-heading font-bold text-slate-800">{b.title}</p>
                  {b.description && <p className="text-xs text-slate-400 mt-0.5">{b.description}</p>}
                  <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-bold px-2.5 py-0.5 font-mono-nums"><Plus className="h-3.5 w-3.5" />{pts(b.points)} نقطة</p>
                </div>
              ))}
          </div>
        </div>
      ) : tab === "rewards" ? (
        <div className="space-y-4" data-testid="loyalty-rewards-tab">
          {canEdit && (
            <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-4 space-y-2">
              <p className="text-sm font-semibold text-slate-700">إضافة مكافأة جديدة</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={newReward.title} onChange={(e) => setNewReward((v) => ({ ...v, title: e.target.value }))} placeholder="اسم المكافأة" className="h-9 text-sm" data-testid="reward-title" />
                <Input type="number" dir="ltr" value={newReward.cost} onChange={(e) => setNewReward((v) => ({ ...v, cost: e.target.value }))} placeholder="التكلفة بالنقاط" className="h-9 text-sm font-mono-nums" data-testid="reward-cost" />
                <Input value={newReward.description} onChange={(e) => setNewReward((v) => ({ ...v, description: e.target.value }))} placeholder="وصف (اختياري)" className="h-9 text-sm" data-testid="reward-desc" />
              </div>
              <div className="flex justify-end"><Button onClick={addReward} disabled={busyReward} size="sm" data-testid="reward-add-btn" className="bg-amber-600 hover:bg-amber-700 gap-1.5"><Plus className="h-4 w-4" /> {busyReward ? "..." : "إضافة"}</Button></div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rewards.length === 0 ? <p className="col-span-full py-8 text-center text-slate-400 text-sm" data-testid="rewards-empty">لا توجد مكافآت بعد. أضف أول مكافأة لفريقك.</p>
              : rewards.map((r) => (
                <div key={r.id} data-testid={`reward-card-${r.id}`} className="relative rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-rose-500 text-white"><Gift className="h-5 w-5" /></div>
                    {canEdit && <button onClick={() => delReward(r.id)} data-testid={`reward-del-${r.id}`} className="grid h-7 w-7 place-items-center rounded-md text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                  <p className="mt-3 font-heading font-bold text-slate-800">{r.title}</p>
                  {r.description && <p className="text-xs text-slate-400 mt-0.5">{r.description}</p>}
                  <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-sm font-bold px-2.5 py-0.5 font-mono-nums"><Coins className="h-3.5 w-3.5" />{pts(r.cost)} نقطة</p>
                </div>
              ))}
          </div>
        </div>
      ) : tab === "requests" ? (
        <div className="space-y-3" data-testid="loyalty-requests-tab">
          {requests.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-14 text-center" data-testid="requests-empty">
              <ClipboardList className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-2 text-slate-500 text-sm">لا توجد طلبات استبدال قيد المراجعة.</p>
              <p className="text-[11px] text-slate-400 mt-1">عندما يطلب المندوب مكافأة من بوابته تظهر هنا لاعتمادها.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {requests.map((r) => {
                const enough = r.balance >= r.cost;
                return (
                  <div key={r.id} data-testid={`request-row-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800">{r.employee_name || "—"} {r.branch && <span className="text-[10px] text-slate-400">· {r.branch}</span>}</p>
                      <p className="text-sm text-slate-600">يطلب: <b>{r.reward_title}</b> · <span className="font-mono-nums text-amber-700 font-bold">{pts(r.cost)} نقطة</span></p>
                      <p className={`text-[11px] ${enough ? "text-emerald-600" : "text-rose-600"}`}>رصيده: {pts(r.balance)} نقطة {enough ? "· كافٍ" : "· غير كافٍ للاعتماد"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" disabled={busyReq === r.id || !enough} data-testid={`request-approve-${r.id}`} onClick={() => decideReq(r.id, true)} className="bg-emerald-600 hover:bg-emerald-700 gap-1"><CheckCircle2 className="h-4 w-4" /> اعتماد</Button>
                      <Button size="sm" variant="outline" disabled={busyReq === r.id} data-testid={`request-reject-${r.id}`} onClick={() => decideReq(r.id, false)} className="text-rose-600 border-rose-200 hover:bg-rose-50 gap-1"><XCircle className="h-4 w-4" /> رفض</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <PartnerOffersPanel isOwner={isOwner} />
      )}

      {award && <AwardDialog member={award} behaviors={behaviors} onClose={() => setAward(null)} onSaved={() => { setAward(null); load(); }} />}
      {redeem && <RedeemDialog member={redeem} rewards={rewards} onClose={() => setRedeem(null)} onSaved={() => { setRedeem(null); load(); }} />}
      {history && <HistoryDialog member={history} onClose={() => setHistory(null)} />}
      {companyRedeem && <CompanyRedeemDialog company={company} onClose={() => setCompanyRedeem(false)} onSaved={() => { setCompanyRedeem(false); load(); }} />}
      {autoOpen && <AutoAwardDialog month={month} onClose={() => setAutoOpen(false)} onDone={() => { load(); loadBoard(); }} />}
    </div>
  );
};
